import * as React from 'react';

import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogTrigger } from '@radix-ui/react-dialog';
import { useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import { useGetInParkingCard } from 'src/features/cards/api/useGetInParkingCard';
import useCalculateStayTime from 'src/features/cards/hooks/useCalculateStayTime';
import { CARD_TYPES, type InParkingCard } from 'src/features/cards/types/card';
import { cardMessages } from 'src/features/cards/utils/messages';
import { ValidateModal } from 'src/features/validation/components/ValidateModal.tsx';
import { useValidatorSettingsCurrentUser } from 'src/features/validation/hooks/useValidatorSettingsCurrentUser.ts';
import { validationMessages } from 'src/features/validation/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import ImageModal from 'src/registry/ui/ImageModal';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { generalMessages } from 'src/utils/messages';

type ValidationDetailsProps = {
    inParkingCard?: InParkingCard;
    refetch: () => void;
};

type ValidationTitleData = {
    title: string;
    value: string | null | undefined;
    control: any;
};
export function ValidationDetails(props: ValidationDetailsProps) {
    const { inParkingCard, refetch: tableRefetch } = props;

    const [stayTime, setStayTime] = React.useState('');

    const { parkingId } = useParking();
    const { calculateStayTime } = useCalculateStayTime();
    const { formatMessage, formatNumber } = useIntl();
    const { data: validatorSettings } = useValidatorSettingsCurrentUser({ parkingId });

    const {
        data: cardData,
        isLoading,
        refetch: cardRefetch,
    } = useGetInParkingCard({
        parkingId,
        id: inParkingCard?.id,
        config: { enabled: !!inParkingCard },
    });

    React.useEffect(() => {
        const stayString = calculateStayTime(cardData);
        setStayTime(stayString);

        const interval = setInterval(() => {
            const stayString = calculateStayTime(cardData);
            setStayTime(stayString);
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, [calculateStayTime, cardData]);

    const refetch = () => {
        tableRefetch();
        void cardRefetch();
    };

    const totalDueFormatted: string =
        (cardData &&
            formatNumber(cardData.due_amount_with_tax / 100, {
                style: 'currency',
                currency: 'BGN',
            })) ||
        '';

    const sidePanelBodyAttributeMapping: ValidationTitleData[] = [
        { title: formatMessage(cardMessages.cardNumber), value: inParkingCard?.plate_number, control: inParkingCard },
        { title: formatMessage(cardMessages.ticket), value: cardData?.card_number, control: cardData },
        { title: formatMessage(cardMessages.parkingZone), value: inParkingCard?.parking_name, control: inParkingCard },
        {
            title: formatMessage(cardMessages.chargePlan),
            value: inParkingCard?.charge_plan_name || '-',
            control: inParkingCard,
        },
        {
            title: formatMessage(cardMessages.enterDatetime),
            value: getFormattedDateTimeForDetailPanel(cardData?.enter_datetime),
            control: cardData,
        },
        { title: formatMessage(cardMessages.currentStay), value: stayTime, control: cardData },
        { title: formatMessage(cardMessages.dueAmount), value: totalDueFormatted, control: !isLoading && cardData },
    ];

    const sidePanelTitle: string =
        inParkingCard?.plate_number === 'NO_PLATE' ? inParkingCard.card_number : inParkingCard?.plate_number || '';

    const isValidateBtnDisabled =
        validatorSettings?.allowed_validation === null ||
        validatorSettings?.allowed_validation === undefined ||
        validatorSettings?.allowed_validation === '' ||
        cardData?.card_type !== CARD_TYPES.NORMAL ||
        inParkingCard?.is_guest === true;

    const ValidateBadge = () => {
        if (!cardData || !inParkingCard) return null;
        const showBadge = cardData?.card_type !== CARD_TYPES.NORMAL || inParkingCard?.is_guest === true;
        if (!showBadge) return null;

        const badgeColor = inParkingCard?.is_guest === true ? 'green' : 'red';
        const badgeIcon = inParkingCard?.is_guest === true ? faCheck : faXmark;
        const badgeText =
            cardData?.card_type !== CARD_TYPES.NORMAL
                ? validationMessages.cannotBeValidated
                : validationMessages.vehicleIsValidated;

        return (
            <Badge theme={badgeColor}>
                <FontAwesomeIcon icon={badgeIcon} style={{ color: badgeColor }} />
                {formatMessage(badgeText)}
            </Badge>
        );
    };

    const imagePath = `/parkings/images/${parkingId}/${cardData?.photo_path}`;

    return (
        <>
            {inParkingCard ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{sidePanelTitle}</DialogTitle>
                    </div>
                    <DialogDescription>{inParkingCard.entry_point}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}
            <DialogSeparator />
            <DialogBody>
                <ImageModal
                    image={imagePath}
                    alt={inParkingCard?.card_number}
                    title={inParkingCard?.card_number}
                    disableClick={!cardData?.photo_path}
                />
                <div className="mt-4 grid gap-2">
                    {sidePanelBodyAttributeMapping.map((details) => {
                        return (
                            <SidePanelBodyAttribute key={details.title}>
                                <SidePanelBodyAttributeTitle>{details.title}</SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={details.control}>
                                    {details.value}
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>
                        );
                    })}
                </div>
            </DialogBody>
            <DialogSeparator />
            <DialogFooter className="flex flex-col gap-2 sm:flex-col">
                <ValidateBadge />
                {inParkingCard && validatorSettings && (
                    <ValidateModal
                        inParkingCard={inParkingCard}
                        refetch={refetch}
                        validatorSettings={validatorSettings}
                    >
                        <DialogTrigger asChild>
                            <Button variant="default" className="flex-1" disabled={isValidateBtnDisabled}>
                                {formatMessage(validationMessages.validate)}
                            </Button>
                        </DialogTrigger>
                    </ValidateModal>
                )}
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
}
